/* eslint-disable no-console */
import {
  IAutomationConfiguration,
  IAutomation,
  AnyAutomation,
  AutomationStatus,
  TreatmentStepCollection,
  AppointmentStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  Timezone,
  asyncForEach,
  collectionGroupQuery,
  bufferedQuery$,
  Firestore,
  runTransaction,
  Transaction,
  where,
  DocumentReference,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { Automation } from '../models/automation/automation';
import { AutomationConfiguration } from '../models/automation/automation-configuration';
import { TimezoneResolver } from '../timezone';

export class UpdateAutomationsFromConfig {
  async run(
    config: WithRef<IAutomationConfiguration>,
    dryRun = true
  ): Promise<void> {
    const timezone = config.practiceRef
      ? await TimezoneResolver.fromPracticeRef(config.practiceRef)
      : await TimezoneResolver.fromBrandRef(
          Firestore.getParentDocRef(config.ref)
        );

    await this._getExistingAutomations$(config.ref)
      .pipe(
        concatMap((automations) =>
          this._updateAutomations(automations, config, timezone, dryRun)
        )
      )
      .toPromise();
  }

  private async _updateAutomations(
    automations: WithRef<IAutomation<AnyAutomation>>[],
    config: WithRef<IAutomationConfiguration>,
    timezone: Timezone,
    dryRun: boolean
  ): Promise<void> {
    await runTransaction((atomicTransaction) => {
      if (!AutomationConfiguration.isActive(config)) {
        return asyncForEach(automations, (automation) =>
          this._setAutomationDisabled(automation, dryRun, atomicTransaction)
        );
      }

      return asyncForEach(automations, (automation) =>
        this._setAutomationActive(
          automation,
          config,
          timezone,
          dryRun,
          atomicTransaction
        )
      );
    });
  }

  private async _setAutomationActive(
    automation: WithRef<IAutomation<AnyAutomation>>,
    config: WithRef<IAutomationConfiguration>,
    timezone: Timezone,
    dryRun: boolean,
    atomicTransaction?: Transaction
  ): Promise<void> {
    if (this._alreadyRunOrCancelled(automation)) {
      return;
    }

    if (config.practiceRef) {
      const appointment = await Automation.resolveAppointment(automation);
      if (
        appointment?.event &&
        !AutomationConfiguration.isForPractice(
          config,
          appointment.event.practice.ref
        )
      ) {
        return;
      }
    }

    const updates = await restoreDisabledAutomationTriggerDate(
      automation,
      timezone
    );
    const status =
      updates.status === AutomationStatus.Cancelled
        ? AutomationStatus.Cancelled
        : AutomationStatus.Pending;

    if (dryRun) {
      console.log(`Updating automation ${automation.ref.path} to ${status}`);
      return;
    }
    await Firestore.patchDoc(
      automation.ref,
      {
        status,
        triggerDate: updates.triggerDate,
      },
      atomicTransaction
    );
  }

  private async _setAutomationDisabled(
    automation: WithRef<IAutomation<AnyAutomation>>,
    dryRun: boolean,
    atomicTransaction?: Transaction
  ): Promise<void> {
    if (
      this._alreadyRunOrCancelled(automation) ||
      automation.status === AutomationStatus.Disabled
    ) {
      return;
    }

    if (dryRun) {
      console.log(`Disabling automation ${automation.ref.path}`);
      return;
    }
    await Firestore.patchDoc(
      automation.ref,
      { status: AutomationStatus.Disabled },
      atomicTransaction
    );
  }

  private _alreadyRunOrCancelled(
    automation: WithRef<IAutomation<AnyAutomation>>
  ): boolean {
    const hasAlreadyRunOrCancelled = [
      AutomationStatus.Cancelled,
      AutomationStatus.Completed,
      AutomationStatus.Failed,
      AutomationStatus.Skipped,
    ];
    return hasAlreadyRunOrCancelled.includes(automation.status);
  }

  private _getExistingAutomations$(
    configRef: DocumentReference<IAutomationConfiguration>
  ): Observable<WithRef<IAutomation<AnyAutomation>>[]> {
    return bufferedQuery$(
      collectionGroupQuery<IAutomation<AnyAutomation>>(
        TreatmentStepCollection.Automations,
        where('configRef', '==', configRef)
      ),
      100,
      'ref'
    );
  }
}

export async function restoreDisabledAutomationTriggerDate(
  automation: WithRef<IAutomation<AnyAutomation>>,
  timezone: Timezone
): Promise<WithRef<IAutomation<AnyAutomation>>> {
  if (
    automation.status !== AutomationStatus.Disabled ||
    automation.triggerDate
  ) {
    return automation;
  }
  const appointment = await Automation.resolveAppointment(automation);
  if (
    !appointment ||
    !appointment.event ||
    appointment.status === AppointmentStatus.Cancelled
  ) {
    return { ...automation, status: AutomationStatus.Cancelled };
  }

  return Automation.adjustStatusAndTriggerDate(
    automation,
    appointment.event,
    timezone
  );
}
